import { useState } from 'react'
import type { DocumentRef } from '../lib/documentReader'
import { formatOfficerDate, formatOfficerDateTime } from '../lib/officerFormat'
import DocumentLink from './documents/DocumentLink'
import { useRead } from './navigation/useRead'
import { EmptyState, ErrorState, Skeleton } from './state'

interface ActivitySection {
  kind: 'processed_without_proposals' | 'automatic_decisions' | 'duplicate_uploads'
  count: number
  documents: DocumentRef[]
}
interface ActivityPage {
  items: {
    id: string
    date: string
    recorded_at: string
    sections: ActivitySection[]
  }[]
  next_cursor: string | null
}
const LABELS: Record<ActivitySection['kind'], string> = {
  processed_without_proposals: 'No new booking',
  automatic_decisions: 'Automatic decisions',
  duplicate_uploads: 'Duplicate uploads',
}

/** Retained summaries are read alongside History; there is no resolve action. */
export default function DailyActivity({ fundId }: { fundId: string | null }) {
  const [cursor, setCursor] = useState<string | null>(null)
  const [refresh, setRefresh] = useState(0)
  const capabilities = useRead<{ enabled: boolean }>('/api/navigation/capabilities', refresh)
  const params = new URLSearchParams({ limit: '10' })
  if (fundId) params.set('fund_id', fundId)
  if (cursor) params.set('cursor', cursor)
  const activity = useRead<ActivityPage>(
    capabilities.value?.enabled ? `/api/navigation/activity?${params}` : null,
    refresh
  )
  if (capabilities.value?.enabled === false) return null
  return (
    <section className="traces-history" aria-label="Daily summaries" data-testid="daily-activity">
      <h2>Daily summaries</h2>
      {cursor ? (
        <button type="button" className="vbtn" onClick={() => setCursor(null)}>
          Latest summaries
        </button>
      ) : null}
      {capabilities.error ||
      (!capabilities.loading && typeof capabilities.value?.enabled !== 'boolean') ? (
        <ErrorState
          what="activity"
          error={capabilities.error ?? new Error('Activity availability could not be read.')}
          onRetry={() => setRefresh((v) => v + 1)}
        />
      ) : activity.error ? (
        <ErrorState
          what="activity"
          error={activity.error}
          onRetry={() => setRefresh((v) => v + 1)}
        />
      ) : capabilities.loading || activity.loading ? (
        <Skeleton kind="rows" n={2} height={41} />
      ) : !activity.value?.items.length ? (
        <EmptyState sentence="No retained summaries in this context." />
      ) : (
        activity.value.items.map((item) => (
          <div className="traces-history-line" key={item.id} data-testid="daily-summary">
            <time dateTime={item.date}>{formatOfficerDate(item.date)}</time>
            <div>
              <small>Recorded automatically · {formatOfficerDateTime(item.recorded_at)}</small>
              {item.sections.map((section) => (
                <div key={section.kind}>
                  <strong>
                    {LABELS[section.kind]} · {section.count}
                  </strong>
                  {section.documents.length ? (
                    <ul>
                      {section.documents.map((document) => (
                        <li key={document.document_id}>
                          <DocumentLink document={document} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      {activity.value?.next_cursor ? (
        <button
          type="button"
          className="vbtn"
          onClick={() => setCursor(activity.value!.next_cursor)}
        >
          Older summaries
        </button>
      ) : null}
    </section>
  )
}
